import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

interface Rating {
  id: string;
  errand_id: string;
  rater_id: string;
  ratee_id: string;
  rating: number;
  review_text?: string;
  review_title?: string;
  rating_type: 'customer_to_driver' | 'driver_to_customer';
  is_anonymous: boolean;
  created_at: string;
  rater?: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
  ratee?: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
  errand?: {
    id: string;
    title: string;
    errand_type?: {
      name: string;
    };
  };
}

interface RatingStats {
  average_rating: number;
  total_ratings: number;
  driver_rating: number;
  driver_rating_count: number;
  customer_rating: number;
  customer_rating_count: number;
}

interface RatingWidgetProps {
  errandId: string;
  rateeId: string;
  rateeName: string;
  ratingType: 'customer_to_driver' | 'driver_to_customer';
  onRatingSubmitted?: () => void;
}

export const RatingWidget: React.FC<RatingWidgetProps> = ({
  errandId,
  rateeId,
  rateeName,
  ratingType,
  onRatingSubmitted
}) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canRate, setCanRate] = useState<boolean | null>(null);
  const [existingRating, setExistingRating] = useState<Rating | null>(null);
  const [ratingReason, setRatingReason] = useState<string>('');

  // Rating categories for detailed feedback
  const ratingCategories = {
    punctuality: { label: 'Punctuality', value: 0 },
    communication: { label: 'Communication', value: 0 },
    professionalism: { label: 'Professionalism', value: 0 },
    quality: { label: 'Service Quality', value: 0 }
  };
  const [detailedRatings, setDetailedRatings] = useState(ratingCategories);

  useEffect(() => {
    checkCanRate();
  }, [errandId, rateeId, user]);

  const checkCanRate = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/ratings/can-rate/${errandId}/${rateeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCanRate(data.canRate);

        // If they can rate, check if they already have a rating
        if (data.canRate) {
          await checkExistingRating();
        }
      }
    } catch (error) {
      console.error('Error checking rating permission:', error);
    }
  };

  const checkExistingRating = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/ratings/errand/${errandId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userRating = data.ratings.find((r: Rating) =>
          r.rater_id === user?.id && r.ratee_id === rateeId
        );
        if (userRating) {
          setExistingRating(userRating);
          setRating(userRating.rating);
          setReviewTitle(userRating.review_title || '');
          setReviewText(userRating.review_text || '');
          setIsAnonymous(userRating.is_anonymous);
        }
      }
    } catch (error) {
      console.error('Error checking existing rating:', error);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/ratings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          errand_id: errandId,
          ratee_id: rateeId,
          rating_type: ratingType,
          rating,
          review_text: reviewText.trim() || null,
          review_title: reviewTitle.trim() || null,
          is_anonymous: isAnonymous
        })
      });

      if (response.ok) {
        toast.success(existingRating ? 'Rating updated successfully!' : 'Rating submitted successfully!');
        setExistingRating(null); // Reset to show success state
        onRatingSubmitted?.();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Network error - please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (canRate === null) {
    return <div className="text-center py-4">Loading...</div>;
  }

  if (!canRate) {
    return (
      <div className="text-center py-4 text-gray-500">
        Rating not available for this errand
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Rate {rateeName}
      </h3>

      {/* Star Rating */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rating *
        </label>
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className="text-2xl focus:outline-none"
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(star)}
            >
              <span className={`${
                star <= (hoverRating || rating)
                  ? 'text-yellow-400'
                  : 'text-gray-300'
              }`}>
                ★
              </span>
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {rating > 0 && `${rating} star${rating !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Review Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Review Title (Optional)
        </label>
        <input
          type="text"
          value={reviewTitle}
          onChange={(e) => setReviewTitle(e.target.value)}
          placeholder="Summarize your experience..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          maxLength={100}
        />
      </div>

      {/* Review Text */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Review (Optional)
        </label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Share details of your experience..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          maxLength={500}
        />
        <p className="text-sm text-gray-500 mt-1">
          {reviewText.length}/500 characters
        </p>
      </div>

      {/* Anonymous Option */}
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">
            Post anonymously
          </span>
        </label>
      </div>

      {/* Trust & Verification Notice */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-center space-x-2">
          <span className="text-blue-600">✓</span>
          <p className="text-sm text-blue-800">
            Your rating helps build trust in our community. All ratings are verified after service completion.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmitRating}
        disabled={isSubmitting || rating === 0}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Submitting...' : existingRating ? 'Update Rating' : 'Submit Rating'}
      </button>

      {existingRating && (
        <p className="text-sm text-gray-500 mt-2 text-center">
          You rated this {new Date(existingRating.created_at).toLocaleDateString()}
        </p>
      )}

      {/* Additional Trust Indicators */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>🔒 Secure & Anonymous Option Available</span>
          <span>📊 Used for Quality Improvement</span>
        </div>
      </div>
    </div>
  );
};

interface RatingDisplayProps {
  userId: string;
  showStats?: boolean;
}

export const RatingDisplay: React.FC<RatingDisplayProps> = ({ userId, showStats = true }) => {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'customer_to_driver' | 'driver_to_customer'>('all');

  useEffect(() => {
    fetchRatings();
  }, [userId, filter]);

  const fetchRatings = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const url = `${API_ENDPOINTS.BASE}/api/ratings/user/${userId}${filter !== 'all' ? `?type=${filter}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRatings(data.ratings || []);
        setStats(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading ratings...</div>;
  }

  return (
    <div className="space-y-6">
      {showStats && stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Rating Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">
                {stats.average_rating.toFixed(1)}
              </div>
              <div className="flex justify-center mb-1">
                {renderStars(Math.round(stats.average_rating))}
              </div>
              <div className="text-sm text-gray-600">
                Overall Rating ({stats.total_ratings} reviews)
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-500">
                {stats.driver_rating.toFixed(1)}
              </div>
              <div className="flex justify-center mb-1">
                {renderStars(Math.round(stats.driver_rating))}
              </div>
              <div className="text-sm text-gray-600">
                As Driver ({stats.driver_rating_count} reviews)
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-500">
                {stats.customer_rating.toFixed(1)}
              </div>
              <div className="flex justify-center mb-1">
                {renderStars(Math.round(stats.customer_rating))}
              </div>
              <div className="text-sm text-gray-600">
                As Customer ({stats.customer_rating_count} reviews)
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Reviews</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('customer_to_driver')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  filter === 'customer_to_driver'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Driver Reviews
              </button>
              <button
                onClick={() => setFilter('driver_to_customer')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  filter === 'driver_to_customer'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Customer Reviews
              </button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {ratings.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No reviews yet
            </div>
          ) : (
            ratings.map((rating) => (
              <div key={rating.id} className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {rating.rater?.avatar_url ? (
                      <img
                        src={rating.rater.avatar_url}
                        alt={rating.rater.full_name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 font-medium">
                          {rating.rater?.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {renderStars(rating.rating)}
                      <span className="text-sm text-gray-500">
                        {rating.is_anonymous ? 'Anonymous' : rating.rater?.full_name}
                      </span>
                      <span className="text-sm text-gray-500">•</span>
                      <span className="text-sm text-gray-500">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </span>
                      {rating.errand?.errand_type && (
                        <>
                          <span className="text-sm text-gray-500">•</span>
                          <span className="text-sm text-gray-500">
                            {rating.errand.errand_type.name}
                          </span>
                        </>
                      )}
                    </div>
                    {rating.review_title && (
                      <h4 className="font-medium text-gray-900 mb-1">
                        {rating.review_title}
                      </h4>
                    )}
                    {rating.review_text && (
                      <p className="text-gray-700">{rating.review_text}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
